import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CacheService, Paginated, Period, TimeUnit } from '@web/birdz-angular';
import { KpiOverview } from 'app/interfaces/kpi-overview.interface';
import { Kpi } from 'app/interfaces/kpi.interface';
import { MapPds } from 'app/interfaces/map-pds.interface';
import { MapResponse } from 'app/interfaces/map-response.interface';
import { PdsKpiHistory } from 'app/interfaces/pds-kpi-history.interface';
import { PdsLight } from 'app/interfaces/pds-light.interface';
import { PdsExportRequest, PdsRequest } from 'app/interfaces/pds-request.interface';
import { Pds } from 'app/interfaces/pds.interface';
import { format } from 'date-fns';
import { environment } from 'environments/environment';
import { map, Observable, of, tap } from 'rxjs';
import { DashboardStats } from '../interfaces/dashboard-stats.interface';
import { KpiRateHistory } from '../interfaces/kpi-rate-history.interface';
import { Perimeter } from '../interfaces/perimeter.interface';

@Injectable({
  providedIn: 'root',
})
export class Api {
  private http = inject(HttpClient);
  private cache = inject(CacheService);
  private _cachedPdsList = new Set<PdsLight>();

  statsUrl = `${environment.apiUrl}/stats`;
  contractKpisUrl = `${environment.apiUrl}/contract-kpis`;
  kpiOverviewUrl = `${environment.apiUrl}/kpi-overview`;
  kpiRateHistoryUrl = `${environment.apiUrl}/kpi-rate-history`;
  pdsCountUrl = `${environment.apiUrl}/pds-count`;
  mapUrl = `${environment.apiUrl}/map`;
  kpiMapUrl = `${environment.apiUrl}/kpi-map`;
  pdsIndexUrl = `${environment.apiUrl}/pds-index`;
  pdsKpiHistoryUrl = `${environment.apiUrl}/pds-kpi-history`;
  pdsUrl = `${environment.apiUrl}/pds`;
  searchPdsUrl = `${environment.apiUrl}/search-pds`;

  constructor() {
    this.cache.register(
      { method: 'post', url: this.statsUrl },
      { method: 'post', url: this.pdsCountUrl },
      { method: 'post', url: this.mapUrl },
      { method: 'post', url: this.kpiMapUrl },
      { method: 'post', url: this.pdsUrl },
      { method: 'post', url: this.pdsIndexUrl },
      { method: 'post', url: this.pdsKpiHistoryUrl },
      { method: 'post', url: this.kpiOverviewUrl },
      { method: 'post', url: this.kpiRateHistoryUrl },
      { method: 'get', url: this.contractKpisUrl },
      { method: 'post', url: this.searchPdsUrl }
    );
  }

  fetchDashboardStatistics(perimeter: Perimeter): Observable<DashboardStats> {
    return this.http.post<DashboardStats>(this.statsUrl, { ...perimeter });
  }

  fetchContractKpis(contract_id: string, techno: string, forDetailsOrMap: boolean): Observable<Kpi[]> {
    const params = {
      contract_id,
      techno,
      for_details_or_map: forDetailsOrMap ? 1 : 0,
    };
    return this.http.get<Kpi[]>(`${this.contractKpisUrl}`, { params });
  }

  fetchPdsCount(perimeter: Perimeter): Observable<number> {
    // Fetch the actual number only for watermetering
    // Other technos typically don't have enough meters to cause loading issues yet,
    // so we give it a value of 0 to stay under the threshold
    return perimeter.techno === 'watermetering'
      ? this.http.post<number>(`${this.pdsCountUrl}`, { ...perimeter })
      : of(0);
  }

  fetchKpiOverview(perimeter: Perimeter): Observable<KpiOverview> {
    return this.http.post<KpiOverview>(`${this.kpiOverviewUrl}`, { ...perimeter });
  }

  fetchKpiRateHistory(perimeter: Perimeter, period: Period, granularity: TimeUnit): Observable<KpiRateHistory> {
    return this.http.post<KpiRateHistory>(this.kpiRateHistoryUrl, {
      ...perimeter,
      from: format(period.from as Date, 'yyyy-MM-dd'),
      to: format(period.to as Date, 'yyyy-MM-dd'),
      granularity: granularity,
    });
  }

  fetchPds(id: string, techno: string | null, date?: Date | null): Observable<MapPds> {
    // the id is sent in the body: pds ids may contain spaces, which some proxies don't forward well in urls
    const body = { id, techno, ...(date ? { date: format(date, 'yyyy-MM-dd') } : {}) };
    const url = `${environment.apiUrl}/pds`;
    this.cache.register({ method: 'post', url: url });
    return this.http.post<MapPds>(url, body);
  }

  fetchPdsIndex(perimeter: Perimeter, params: PdsRequest): Observable<Paginated<Pds>> {
    return this.http.post<Paginated<Pds>>(this.pdsIndexUrl, {
      ...perimeter,
      ...params,
    });
  }

  fetchPdsKpiHistory(
    perimeter: Perimeter,
    pdsId: string,
    kpi: string,
    from: Date,
    to: Date,
    granularity: 'hour' | 'day' = 'day'
  ): Observable<PdsKpiHistory> {
    return this.http.post<PdsKpiHistory>(this.pdsKpiHistoryUrl, {
      ...perimeter,
      pds_id: pdsId,
      kpi,
      from: format(from, 'yyyy-MM-dd'),
      to: format(to, 'yyyy-MM-dd'),
      granularity,
    });
  }

  exportPdsIndex(perimeter: Perimeter, params: PdsExportRequest): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/pds-export-index`, {
      ...perimeter,
      ...params,
    });
  }

  fetchDashboardMap(perimeter: Perimeter): Observable<MapResponse> {
    return this.http.post<MapResponse>(this.mapUrl, perimeter);
  }

  fetchKpiMap(perimeter: Perimeter, kpi: string, date: Date, granularity: TimeUnit): Observable<MapResponse> {
    return this.http.post<MapResponse>(this.kpiMapUrl, {
      ...perimeter,
      kpi,
      date: format(date, 'yyyy-MM-dd'),
      granularity: granularity,
    });
  }

  search(perimeter: Perimeter, query: string, date?: Date): Observable<PdsLight[]> {
    query = query.toLowerCase().trim();

    const params = {
      ...perimeter,
      q: query,
      ...(date ? { date: format(date, 'yyyy-MM-dd') } : {}),
    };

    return this.http.post<PdsLight[]>(this.searchPdsUrl, params).pipe(
      tap((pdsList: PdsLight[]) => {
        pdsList.forEach((pds) => this._cachedPdsList.add(pds));
      }),
      map((pdsList) => {
        // guess "found_by"
        pdsList.forEach((pds) => {
          if (pds.customer_pds_id?.toLowerCase().includes(query)) {
            pds.found_by = 'customer_pds_id';
          } else if (pds.equipment_id?.toLowerCase().includes(query)) {
            pds.found_by = 'equipment_id';
          } else if (pds.device_id?.toLowerCase().includes(query)) {
            pds.found_by = 'device_id';
          } else {
            pds.found_by = 'id';
          }
        });

        return pdsList;
      })
    );
  }
}
